import React from "react";
import {
  Trophy,
  Users,
  Calendar,
  Target,
  Crown,
  Award,
  TrendingUp,
  Clock,
  Star,
  Zap,
  Shield,
  Play,
} from "lucide-react";

interface HomePageProps {
  onSelectTournament: () => void;
  onSelectLeague: () => void;
  onOpenEventManager: () => void;
  hasExistingEvents: boolean;
}

const HomePage: React.FC<HomePageProps> = ({
  onSelectTournament,
  onSelectLeague,
  onOpenEventManager,
  hasExistingEvents,
}) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center py-8">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-gray-900 mb-2">
                🏓 Pickleball Tournament & League Manager
              </h1>
              <p className="text-lg text-gray-600">
                Professional tournament and league management for pickleball
                clubs
              </p>
              {hasExistingEvents && (
                <div className="mt-4">
                  <button
                    onClick={onOpenEventManager}
                    className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors duration-200"
                  >
                    Manage Existing Events
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Welcome Section */}
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
            Choose Your Event Type
          </h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto px-4">
            Select between one-time tournaments or ongoing leagues. Each format
            is designed for different group sizes and competition styles.
          </p>
        </div>

        {/* Event Type Selection */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 mb-12 sm:mb-16">
          {/* Tournaments Card */}
          <div className="bg-white rounded-xl shadow-lg border-2 border-blue-200 hover:border-blue-400 transition-all duration-300 hover:shadow-xl">
            <div className="p-6 sm:p-8">
              <div className="flex items-center mb-6">
                <div className="bg-blue-100 p-3 rounded-full mr-4">
                  <Trophy className="w-8 h-8 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    Tournaments
                  </h3>
                  <p className="text-blue-600 font-medium">
                    One-time competitive events
                  </p>
                </div>
              </div>

              <p className="text-gray-600 mb-6">
                Perfect for special events, championships, and competitive
                gatherings. Choose from multiple formats designed for different
                group sizes.
              </p>

              <div className="space-y-3 mb-6">
                <div className="flex items-center text-sm text-gray-700">
                  <Clock className="w-4 h-4 mr-2 text-blue-500" />
                  <span>2-3 hour completion time</span>
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <Users className="w-4 h-4 mr-2 text-blue-500" />
                  <span>8-64+ teams supported</span>
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <Target className="w-4 h-4 mr-2 text-blue-500" />
                  <span>Clear winner determination</span>
                </div>
              </div>

              <button
                onClick={onSelectTournament}
                className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center"
              >
                <Play className="w-5 h-5 mr-2" />
                Create Tournament
              </button>
            </div>
          </div>

          {/* Leagues Card */}
          <div className="bg-white rounded-xl shadow-lg border-2 border-purple-200 hover:border-purple-400 transition-all duration-300 hover:shadow-xl">
            <div className="p-6 sm:p-8">
              <div className="flex items-center mb-6">
                <div className="bg-purple-100 p-3 rounded-full mr-4">
                  <Calendar className="w-8 h-8 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Leagues</h3>
                  <p className="text-purple-600 font-medium">
                    Ongoing competitive seasons
                  </p>
                </div>
              </div>

              <p className="text-gray-600 mb-6">
                Ideal for regular club play, skill development, and community
                building. Multiple league formats to match your club's needs and
                schedule.
              </p>

              <div className="space-y-3 mb-6">
                <div className="flex items-center text-sm text-gray-700">
                  <Calendar className="w-4 h-4 mr-2 text-purple-500" />
                  <span>5-18 week seasons</span>
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <Users className="w-4 h-4 mr-2 text-purple-500" />
                  <span>6-24+ teams supported</span>
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <TrendingUp className="w-4 h-4 mr-2 text-purple-500" />
                  <span>Skill progression tracking</span>
                </div>
              </div>

              <button
                onClick={onSelectLeague}
                className="w-full bg-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-purple-700 transition-colors duration-200 flex items-center justify-center"
              >
                <Play className="w-5 h-5 mr-2" />
                Create League
              </button>
            </div>
          </div>
        </div>

        {/* Universal Features */}
        <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Universal Features for All Events
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="bg-green-100 p-3 rounded-full w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                <Zap className="w-6 h-6 text-green-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-1">Live Scoring</h4>
              <p className="text-sm text-gray-600">
                Real-time updates and standings
              </p>
            </div>

            <div className="text-center">
              <div className="bg-blue-100 p-3 rounded-full w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                <Shield className="w-6 h-6 text-blue-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-1">Achievements</h4>
              <p className="text-sm text-gray-600">
                Track perfect games and streaks
              </p>
            </div>

            <div className="text-center">
              <div className="bg-purple-100 p-3 rounded-full w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                <Star className="w-6 h-6 text-purple-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-1">Statistics</h4>
              <p className="text-sm text-gray-600">Comprehensive analytics</p>
            </div>

            <div className="text-center">
              <div className="bg-orange-100 p-3 rounded-full w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                <Award className="w-6 h-6 text-orange-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-1">Awards</h4>
              <p className="text-sm text-gray-600">Recognition system</p>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg p-6 text-center">
            <div className="text-3xl font-bold mb-2">3</div>
            <div className="text-blue-100">Tournament Formats</div>
          </div>

          <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg p-6 text-center">
            <div className="text-3xl font-bold mb-2">6</div>
            <div className="text-purple-100">League Formats</div>
          </div>

          <div className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg p-6 text-center">
            <div className="text-3xl font-bold mb-2">∞</div>
            <div className="text-green-100">Team Capacity</div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default HomePage;
